import { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { FORCED_PERSONAL_DASHBOARD_QUERY_PARAM, isInactiveManagedClubPersonalDashboardEligible } from "@/store/auth";
import { resolveClubRouteContext } from "@/core/utils/clubRouteContext";
import { isViewAsMemberEnabled, notifyMemberViewRedirect } from "@/composables/useViewAsMember";

import { getLogger } from "@/utils/logger";
import { optionalSafeReturnPath } from "@/utils/returnPath";

const logger = getLogger("AuthGuard");

async function retryFailedAuthCheck(authStore: ReturnType<typeof useAuthStore>): Promise<void> {
  if (!authStore.authCheckFailed) return;
  logger.warn("authGuard: Auth check failed; retrying before making an access decision");
  await authStore.checkAuth(true);
}

function hasMatchedMetaFlag(
  to: RouteLocationNormalized,
  metaKey: "public" | "requiresAuth" | "requiresAdmin" | "requiresCompleteProfileWhenAuthenticated",
): boolean {
  if (to.matched.length === 0) {
    return to.meta?.[metaKey] === true;
  }

  return to.matched.some((record) => record.meta?.[metaKey] === true);
}

/**
 * Navigation guard to check if a user is authenticated.
 *
 * Returns a vue-router navigation result rather than calling a `next()`
 * callback (the callback form is deprecated in vue-router 5 and removed in 6):
 * - `true` allows the navigation to continue
 * - a `RouteLocationRaw` (e.g. `{ path: "/login" }`) redirects
 *
 * @param to Route the user is navigating to
 * @param from Route the user is navigating from
 */
export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
): Promise<RouteLocationRaw | boolean> {
  // logger.info("authGuard: Triggered. To:", to.path, "From:", from.path); // Optional: remove for cleaner logs
  const authStore = useAuthStore();
  const isProfileCompletion = to.path.replace(/\/+$/u, "") === "/complete-profile";
  const isUserHome = to.path === "/home" || to.path === "/home/";
  const query = to.query ?? {};
  const redirectQuery = optionalSafeReturnPath(query.redirect);
  const forcedPersonalDashboard =
    (Array.isArray(query[FORCED_PERSONAL_DASHBOARD_QUERY_PARAM])
      ? query[FORCED_PERSONAL_DASHBOARD_QUERY_PARAM][0]
      : query[FORCED_PERSONAL_DASHBOARD_QUERY_PARAM]) === "1";

  // Special handling for profile completion page
  if (isProfileCompletion) {
    // Always force an auth check for this critical page, especially on refresh
    await authStore.checkAuth(true);
    await retryFailedAuthCheck(authStore);

    if (authStore.authCheckFailed && !authStore.isAuthenticated) {
      logger.warn("authGuard: Auth check failed; leaving profile completion navigation unresolved");
      return false;
    }

    // After a fresh auth check, if user is authenticated and needs completion, stay.
    // The component itself handles redirecting if completion is NOT needed.
    if (authStore.isAuthenticated && authStore.needsProfileCompletion) {
      return true;
    }
    // If authenticated but profile IS complete, the component will redirect to the app root.
    // So, if we reach here and isAuthenticated is true, it means needsProfileCompletion is false.
    // The ProfileCompletion component handles this by redirecting there.
    // However, to prevent a flash of the login page if the component hasn't mounted yet,
    // we can also redirect here if profile is complete.
    if (authStore.isAuthenticated && !authStore.needsProfileCompletion) {
      return redirectQuery ? { path: redirectQuery } : { path: "/" };
    }

    // If still not authenticated after a forced check, redirect to login
    return {
      path: "/login",
      query: { redirect: to.fullPath },
    };
  }

  // Wait for initial auth check to complete before making routing decisions
  if (!authStore.initialAuthCheckDone) {
    try {
      let waitAttempts = 0;
      while (!authStore.initialAuthCheckDone && authStore.isLoading && waitAttempts < 200) {
        // Max 10 seconds wait
        await new Promise((resolve) => setTimeout(resolve, 50));
        waitAttempts++;
      }
      if (waitAttempts >= 200 || !authStore.initialAuthCheckDone) {
        logger.warn(
          "authGuard: Timeout or failure waiting for initial auth check. isAuthenticated:",
          authStore.isAuthenticated,
        );
      }
    } catch (error) {
      logger.error(
        "authGuard: Error during initial auth check waiting phase (should be rare if promise rejections are handled in checkAuth):",
        error,
      );
      // Fall through, subsequent logic will redirect to login if not authenticated.
    }
  } else {
    try {
      // logger.info("authGuard: Explicitly checking auth state during navigation"); // Optional: remove for cleaner logs
      await authStore.checkAuth(true);
    } catch (error) {
      logger.error(
        "authGuard: Error during explicit authStore.checkAuth(true). User may be redirected to login:",
        error,
      );
      // Catching to prevent uncaught error. Subsequent checks will handle redirection.
    }
  }

  await retryFailedAuthCheck(authStore);

  // Handle public routes with authenticated user redirection
  const isExplicitlyPublic = !hasMatchedMetaFlag(to, "requiresAuth") && hasMatchedMetaFlag(to, "public");

  const requiresAuth = hasMatchedMetaFlag(to, "requiresAuth") || (!isExplicitlyPublic && !isProfileCompletion);

  if (authStore.authCheckFailed && requiresAuth && !authStore.isAuthenticated) {
    logger.warn("authGuard: Auth check failed; refusing to treat the user as anonymous");
    return false;
  }

  if (isExplicitlyPublic) {
    // Some public entry points support both anonymous discovery and an
    // authenticated action. Keep the route visible to anonymous visitors, but
    // send a signed-in OTP account through profile completion before the action
    // page can use its account identity.
    if (
      authStore.isAuthenticated &&
      authStore.needsProfileCompletion &&
      hasMatchedMetaFlag(to, "requiresCompleteProfileWhenAuthenticated")
    ) {
      return { path: "/complete-profile", query: { redirect: to.fullPath } };
    }
    // Check if authenticated users should be redirected
    if (to.meta.redirectAuthenticatedTo && authStore.isAuthenticated) {
      return { path: to.meta.redirectAuthenticatedTo as string };
    }
    return true;
  }

  // Check if the route requires authentication
  if (requiresAuth && !authStore.isAuthenticated) {
    return {
      path: "/login",
      query: { redirect: to.fullPath },
    };
  }

  if (authStore.isAuthenticated) {
    const profileIncomplete = authStore.needsProfileCompletion;
    if (profileIncomplete && !isProfileCompletion) {
      // logger.info("User needs to complete profile, redirecting"); // Optional: remove for cleaner logs
      return { path: "/complete-profile", query: { redirect: to.fullPath } };
    }

    if (isUserHome) {
      if (forcedPersonalDashboard && isInactiveManagedClubPersonalDashboardEligible(authStore.user)) {
        return true;
      }

      const homeState = authStore.userHomeState;

      if (homeState.type === "find-clubs-redirect") {
        return { path: "/findaclub" };
      }

      if (homeState.type === "manager-club-redirect") {
        return { path: `/club/${homeState.clubShortName}` };
      }
    }

    if (to.meta.requiresOfficer && !authStore.isAdmin) {
      const clubShortNameParam = to.params.clubShortName as string | undefined;
      const clubIdParam = (to.params.clubId as string | undefined) ?? undefined;
      const normalizedRouteClubId = clubIdParam ? Number.parseInt(clubIdParam.replace(/^c/i, ""), 10) : undefined;
      const routeClubId =
        (typeof normalizedRouteClubId === "number" && Number.isFinite(normalizedRouteClubId)
          ? normalizedRouteClubId
          : undefined) ?? resolveClubRouteContext(clubShortNameParam, authStore.user)?.clubId;

      const hasOfficerAccess = authStore.user?.clubMemberships?.some((membership) => {
        // A manager passes because the server's gate (`canManage`) admits managers.
        if (typeof routeClubId === "number" && membership.club.id === routeClubId) {
          return membership.isOfficer || membership.isManager;
        }
        if (clubShortNameParam && membership.club.shortName.toLowerCase() === clubShortNameParam.toLowerCase()) {
          return membership.isOfficer || membership.isManager;
        }
        return false;
      });

      if (!hasOfficerAccess) {
        // If member view is the reason access is masked, surface an actionable
        // notice on the destination instead of a silent bounce.
        if (isViewAsMemberEnabled.value) notifyMemberViewRedirect();
        return { path: "/" };
      }
    }

    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      if (isViewAsMemberEnabled.value) notifyMemberViewRedirect();
      return { path: "/" };
    }

    return true;
  }

  return true;
}
